import { defineComponent as _defineComponent } from 'vue'
import Page from "@/components/shared/Page.vue";
import BaseButton from "@/components/shared/button/base/BaseButton.vue";
import CheckboxInput from "@/components/shared/inputs/CheckboxInput.vue";
import PasswordInput from "@/components/shared/inputs/PasswordInput.vue";
import TextInput from "@/components/shared/inputs/TextInput.vue";
import { ILoginData } from "@/domain/entities/login";
import { ILoginErrorData } from "@/domain/entities/login/loginError.types";
import { ISignupInfo } from "@/domain/entities/serviceInfo/serviceInfo.types";
import router from "@/router";
import { useDeviceStore } from "@/stores/deviceStore";
import { useServiceStore } from "@/stores/serviceStore";
import { useUserStore } from "@/stores/userStore";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_RESIZE_WEBVIEW,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  RemoveLoadingImage,
  ResizeWebview,
  RouterName,
  SESSION_KEY_LOGIN_ID,
  ShowErrorModal,
  ShowLoadingImage,
} from "@/types";
import useSetSessionStorageItem from "@/use/useSetSessionStorageItem";
import { Form } from "vee-validate";
import { ComputedRef, computed, inject, onMounted, ref, watch } from "vue";
import ChangePassword from "../mypage/myinfo/ChangePassword.vue";
import { IResultData } from "@/domain/entities/shared/result";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import { useRequestAuth } from "@/composables/shared/useRequestAuth";


const __default__ = {
  name: "Login",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;
const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const resizeWebviewWithSafeArea = inject(
  PROVIDE_KEY_RESIZE_WEBVIEW
) as ResizeWebview;

const userStore = useUserStore();
const deviceStore = useDeviceStore();
const serviceStore = useServiceStore();

const shouldHideNavBarBtn = computed(
  () =>
    !serviceStore.serviceInfo?.isPublicLineApi &&
    !serviceStore.serviceInfo?.isPublicTalkApi
);

const signUpInfos: ComputedRef<ISignupInfo[]> = computed(
  () => serviceStore.signupInfos ?? []
);
const logoPath = computed(() => serviceStore.serviceInfo?.logoPath);

const passwordField = computed(() =>
  serviceStore.signupInfos?.[0]?.fields?.find(
    (field) => field.key === "JOIN.PASSWORD"
  )
);

const passwordPlaceholder = computed(() =>
  passwordField.value?.placeholder?.split("(")?.[1]?.replace(")", "")
);

const passwordMinLength = computed(
  () => passwordField.value?.passwordMinLength ?? 6
);

const passwordMaxLength = computed(
  () => passwordField.value?.passwordMaxLength ?? 16
);

const schema = {
  id: "required:사원번호를",
  password: {
    required: "비번호를",
    password: [
      passwordField.value?.passwordRegex,
      `영문자 + 숫자 조합으로 ${passwordMinLength.value}~${passwordMaxLength.value}자 사이로 입력해 주세요.`,
    ],
  },
};
const userId = ref("");
const password = ref("");
const isAutoLogin = ref(true);
const loginErrorMessage = ref("");

const onSubmit = async () => {
  showLoadingImage();
  const loginResult = await userStore.login(
    userId.value,
    password.value,
    isAutoLogin.value
  );
  removeLoadingImage();

  loginResult.isSuccess
    ? handleLoginSuccess(loginResult as IResultData<ILoginData>)
    : handleLoginError(loginResult as IResultData<ILoginErrorData>);
};

/** 로그인 요청 성공에 대한 처리 */
const handleLoginSuccess = async (loginResult: IResultData<ILoginData>) => {
  const loginData = loginResult.data as ILoginData;

  if (loginData.isTempPassword) {
    // 임시 비밀번호로 로그인한 경우
    useSetSessionStorageItem(SESSION_KEY_LOGIN_ID, userId.value);
    toggleShowChangePassword(true);
    return;
  }

  if (loginData.isPasswordExpired) {
    // 비밀번호가 만료된 경우
    useSetSessionStorageItem(SESSION_KEY_LOGIN_ID, userId.value);
    router.replace({ name: RouterName.ChangeExpiredPassword });
    return;
  }

  router.replace({ name: RouterName.MainLogin });
};

/** 로그인 요청 실패에 대한 처리 */
const handleLoginError = async (loginResult: IResultData<ILoginErrorData>) => {
  const loginErrorData = loginResult as IResultData<ILoginErrorData>;

  if (loginErrorData.resultCode === 401000010) {
    // 비밀번호가 틀린 경우
    showLoginErrorMessageForInvalidUserInfo();
  } else if (loginErrorData.resultCode === 401000021) {
    // 로그인 에러 처리 - 계정이 존재하지 않습니다.
    showLoginErrorMessageForInvalidUserInfo();
  } else {
    // 그 밖에 이유 - 승인대기, 로그인 실패 횟수 초과 등...
    showLoginErrorModalForOtherReason(loginErrorData);
  }
};

/** 로그인 에러 처리 - 에러 메세지 */
const showLoginErrorMessageForInvalidUserInfo = () => {
  loginErrorMessage.value = "사원번호 또는 비밀번호가 일치하지 않습니다.";
};

/** 로그인 에러 처리 - 그 밖에 이유 */
const showLoginErrorModalForOtherReason = (
  loginErrorData: IResultData<ILoginErrorData>
) => {
  let errorTitle = "로그인 오류";
  let errorMessage = loginErrorData.errorMessage ?? "";
  let callback = undefined;

  if (loginErrorData.resultCode === 401000203) {
    useSetSessionStorageItem(SESSION_KEY_LOGIN_ID, userId.value);
    errorTitle = "로그인 실패";
    callback = goToAuth;
  }

  if (loginErrorData.resultCode === 401000023) {
    errorTitle = "회원가입 승인대기";
    errorMessage = "담당자 승인 후 로그인 하실 수 있습니다.";
  }

  showErrorModal(
    errorTitle,
    errorMessage,
    loginErrorData.resultCode,
    loginErrorData.errorTime,
    callback
  );
};

const shouldShowChangePassword = ref(false);
const toggleShowChangePassword = (show: boolean) => {
  shouldShowChangePassword.value = show;
};

const resetErrorMessage = () => {
  loginErrorMessage.value = "";
};

const goToMainLogin = () => {
  setTimeout(() => {
    router.replace({ name: RouterName.MainLogin });
  }, 300);
};

const { addAuthScript, requestAuth } = useRequestAuth();

const goToAuth = () => {
  resizeWebviewWithSafeArea(true, 100);
  // 본인 인증 화면으로 이동
  requestAuth("unlock");
};

const goToFindLoginInfo = () => {
  router.push({ name: RouterName.FindLoginInfo });
};

const goToTermsOfUse = () => {
  router.push({ name: RouterName.TermsOfUse });
};

const goToTypesOfSignUp = () => {
  router.push({ name: RouterName.TypesOfSignUp });
};

const onClickSingUp = () => {
  if (signUpInfos.value.length > 1) {
    goToTypesOfSignUp();
  } else {
    goToTermsOfUse();
  }
};

const goToMainLogout = () => {
  router.replace({ name: RouterName.MainLogout });
};

const isShowingKeyboard = ref(false);
watch(
  () => deviceStore.isShowingKeyboard,
  (isShowing, _) => {
    isShowingKeyboard.value = isShowing;
  }
);

onMounted(() => {
  addAuthScript();
});

const __returned__ = { showLoadingImage, removeLoadingImage, showErrorModal, resizeWebviewWithSafeArea, userStore, deviceStore, serviceStore, shouldHideNavBarBtn, signUpInfos, logoPath, passwordField, passwordPlaceholder, passwordMinLength, passwordMaxLength, schema, userId, password, isAutoLogin, loginErrorMessage, onSubmit, handleLoginSuccess, handleLoginError, showLoginErrorMessageForInvalidUserInfo, showLoginErrorModalForOtherReason, shouldShowChangePassword, toggleShowChangePassword, resetErrorMessage, goToMainLogin, addAuthScript, requestAuth, goToAuth, goToFindLoginInfo, goToTermsOfUse, goToTypesOfSignUp, onClickSingUp, goToMainLogout, isShowingKeyboard, Page, BaseButton, CheckboxInput, PasswordInput, TextInput, get Form() { return Form }, ChangePassword, NavigationBar }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})